import { useState, type ReactNode } from 'react';
import type { ApiFailure } from '../api/client.js';
import { REVIEW_SEVERITIES, type Problem } from './forms.js';
import { severityLabel, shortId } from './labels.js';

/**
 * The console's primitives. Written for the business team: failures are
 * sentences with a small reference code for support, identifiers are short
 * with a Copy control, and nothing here shows a raw payload.
 */

export function Head({ title, lede, meta }: { title: string; lede?: ReactNode; meta?: ReactNode }) {
  return (
    <div className="cr-head">
      <div>
        <h1 className="cr-title">{title}</h1>
        {lede !== undefined && <p className="cr-lede">{lede}</p>}
      </div>
      {meta !== undefined && <div className="cr-meta">{meta}</div>}
    </div>
  );
}

export function Card({ title, aside, children }: { title?: string; aside?: ReactNode; children: ReactNode }) {
  return (
    <section className="cr-card">
      {(title !== undefined || aside !== undefined) && (
        <div className="cr-card-head">
          {title !== undefined ? <h2>{title}</h2> : <span />}
          {aside}
        </div>
      )}
      {children}
    </section>
  );
}

export type Tone = 'ok' | 'warn' | 'bad' | 'brand';

export function Tag({ children, tone }: { children: ReactNode; tone?: Tone }) {
  return <span className={tone === undefined ? 'cr-badge' : `cr-badge ${tone}`}>{children}</span>;
}

/** The severity vocabulary's weight, in colour. Unknown values stay neutral. */
export function severityTone(severity: string | null | undefined): Tone | undefined {
  switch (severity) {
    case 'ncii_suspected':
    case 'explicit_unmatched':
      return 'bad';
    case 'unassessed':
      return 'warn';
    case 'benign_copy':
    case 'likely_not_subject':
      return 'ok';
    default:
      return undefined;
  }
}

/** Any wire value as text; `null`/`undefined` read as "not reported", never as 0 or blank. */
export function show(value: unknown): ReactNode {
  if (value === null || value === undefined) return <span className="cr-muted">—</span>;
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (typeof value === 'number' || typeof value === 'string') return String(value);
  if (Array.isArray(value) && value.every((item) => typeof item === 'string' || typeof item === 'number')) {
    return value.map(String).join(', ');
  }
  return <span className="cr-mono">{JSON.stringify(value)}</span>;
}

export function Empty({ children }: { children: ReactNode }) {
  return <p className="cr-empty">{children}</p>;
}

export function Loading() {
  return <p className="cr-empty">Loading…</p>;
}

/** A shortened identifier with the full value one click away. */
export function IdChip({ value, href, what = 'ID' }: { value: string; href?: string; what?: string }) {
  const [copied, setCopied] = useState(false);
  const copy = () => {
    const clipboard = typeof navigator === 'undefined' ? undefined : navigator.clipboard;
    if (clipboard === undefined) return;
    clipboard
      .writeText(value)
      .then(() => {
        setCopied(true);
        window.setTimeout(() => setCopied(false), 1400);
      })
      .catch(() => undefined);
  };
  return (
    <span className="cr-id" title={value}>
      {href === undefined ? (
        <span className="cr-mono">{shortId(value)}</span>
      ) : (
        <a href={href} className="cr-mono">
          {shortId(value)}
        </a>
      )}
      <button type="button" className="cr-copy" onClick={copy} aria-label={`Copy ${what}`}>
        {copied ? 'Copied' : 'Copy'}
      </button>
    </span>
  );
}

interface TroubleCopy {
  title: string;
  detail?: string;
}

/** Every failure a person can meet here, in that person's words. */
function troubleCopy(error: ApiFailure): TroubleCopy {
  const wait = error.retryAfter === null ? null : `${String(error.retryAfter)} second${error.retryAfter === 1 ? '' : 's'}`;
  switch (error.code) {
    case 'NOT_AN_OPERATOR':
      return {
        title: 'Your account does not have operator access.',
        detail:
          'Ask the ImageShield engineering team to grant it. Once they have, choose Check again — you will not need to sign in again.',
      };
    case 'ADMIN_UPSTREAM_ERROR':
      return {
        title: 'ImageShield could not reach its monitoring services.',
        detail: 'Nothing was changed. Try again in a minute, and tell engineering if it keeps happening.',
      };
    case 'VALIDATION_FAILED':
      return { title: 'Some of this information was not accepted.', detail: 'Check the fields and try again. Nothing was changed.' };
    case 'RATE_LIMITED':
      return { title: 'Too many attempts.', detail: wait === null ? 'Try again shortly.' : `Try again in ${wait}.` };
    case 'TRANSPORT':
      return { title: 'ImageShield cannot be reached right now.', detail: 'Check your connection and try again.' };
    case 'NON_ENVELOPE_RESPONSE':
      return { title: 'The service returned an unexpected response.', detail: 'Try again in a moment; tell engineering if it keeps happening.' };
    case 'REVIEW_TASK_NOT_FOUND':
      return { title: 'This task has already been decided or removed.' };
    case 'SCORE_NOT_FOUND':
      return { title: 'No score exists for this person yet.' };
    case 'ARTICLE_NOT_FOUND':
      return { title: 'This article could not be found.' };
    case 'THREAT_EVENT_NOT_FOUND':
      return { title: 'This threat event could not be found, or it is no longer active.' };
    case 'PROVIDER_NOT_FOUND':
      return { title: 'This provider could not be found.' };
    case 'NOT_FOUND':
      return { title: 'That could not be found.' };
    case 'CONFLICT':
      return { title: 'This change conflicts with the current state.', detail: 'Refresh and try again.' };
    case 'UNAUTHORISED':
      return { title: 'Your session has ended.', detail: 'Sign in again to continue.' };
    case 'INVALID_CODE':
      return { title: 'That code is not right.', detail: 'Check the six digits and try again.' };
    case 'CHALLENGE_EXPIRED':
      return { title: 'That code has expired.', detail: 'Request a new one below.' };
    case 'TOO_MANY_ATTEMPTS':
      return { title: 'Too many attempts with this code.', detail: 'Request a new one below.' };
    case 'SMS_DELIVERY_FAILED':
      return { title: 'The code could not be sent to that number.', detail: 'Check the number, or try again shortly.' };
    case 'PHONE_COOLDOWN':
      return { title: 'This number cannot sign in yet.', detail: error.message };
    default:
      return { title: error.message.charAt(0).toUpperCase() + error.message.slice(1) };
  }
}

/** A failure, as a sentence — plus a small reference so support can find it in the logs. */
export function Trouble({ error }: { error: ApiFailure }) {
  const copy = troubleCopy(error);
  return (
    <div className="cr-trouble" role="alert">
      <strong>{copy.title}</strong>
      {copy.detail !== undefined && <span>{copy.detail}</span>}
      <small>
        Reference {error.code}
        {error.status > 0 ? ` · ${String(error.status)}` : ''}
      </small>
    </div>
  );
}

export function Problems({ problems }: { problems: Problem[] }) {
  if (problems.length === 0) return null;
  return (
    <ul className="cr-problems" role="alert">
      {problems.map((problem) => (
        <li key={problem.field}>{problem.message}</li>
      ))}
    </ul>
  );
}

export function problemFor(problems: Problem[], field: string): Problem | undefined {
  return problems.find((problem) => problem.field === field);
}

export function Field({
  label,
  hint,
  problem,
  children,
}: {
  label: ReactNode;
  hint?: ReactNode;
  problem?: Problem | undefined;
  children: ReactNode;
}) {
  return (
    <label className={problem === undefined ? 'cr-field' : 'cr-field bad'}>
      <span>
        {label}
        {hint !== undefined && <small>{hint}</small>}
      </span>
      {children}
      {problem !== undefined && <em>{problem.message}</em>}
    </label>
  );
}

/** The API's own bounds for a reason (3–500 characters). */
const REASON_MIN = 3;
const REASON_MAX = 500;

/**
 * A reason and the action it authorises, together. Every switch, retraction
 * and archive here is recorded with a reason in the audit log, so the reason
 * is part of the action — the button wakes up once there is one.
 */
export function ReasonStrip({
  verb,
  tone,
  pending,
  disabled,
  placeholder,
  onRun,
}: {
  verb: string;
  tone: 'danger' | 'primary';
  pending: boolean;
  disabled?: boolean;
  placeholder?: string;
  onRun: (reason: string) => void;
}) {
  const [reason, setReason] = useState('');
  const trimmed = reason.trim();
  const ready = trimmed.length >= REASON_MIN && trimmed.length <= REASON_MAX;
  return (
    <div className="cr-strip">
      <span className="cr-strip-label">Reason</span>
      <input
        type="text"
        value={reason}
        maxLength={REASON_MAX}
        placeholder={placeholder ?? 'Why — recorded with this action'}
        disabled={pending || disabled === true}
        aria-label={`Reason to ${verb.toLowerCase()}`}
        onChange={(event) => setReason(event.target.value)}
        onKeyDown={(event) => {
          if (event.key === 'Enter' && ready && !pending) onRun(trimmed);
        }}
      />
      <button
        type="button"
        className={`cr-btn ${tone}`}
        disabled={!ready || pending || disabled === true}
        onClick={() => onRun(trimmed)}
      >
        {pending ? `${verb}…` : verb}
      </button>
    </div>
  );
}

/**
 * The queue's depth per severity, in the queue's own order: the services fix
 * the priority (most serious first), so the order carries meaning.
 */
export function Lanes({ queue }: { queue: Record<string, number> }) {
  const known: readonly string[] = REVIEW_SEVERITIES;
  const names = [...known, ...Object.keys(queue).filter((name) => !known.includes(name))];
  const max = Math.max(1, ...Object.values(queue));
  return (
    <ol className="cr-lanes">
      {names.map((name, index) => {
        const count = queue[name] ?? 0;
        const hot = index < 2 && count > 0;
        return (
          <li key={name} className={hot ? 'cr-lane hot' : 'cr-lane'}>
            <span className="cr-lane-name">{severityLabel(name)}</span>
            <span className="cr-lane-track" aria-hidden="true">
              <span className="cr-lane-fill" style={{ width: `${String((count / max) * 100)}%` }} />
            </span>
            <span className="cr-lane-count">{count}</span>
          </li>
        );
      })}
    </ol>
  );
}

/** Navigate within the console. Hash only — the pathname stays /admin. */
export function go(hash: string): void {
  window.location.hash = hash;
}
